import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '~/api/client'
import {
    type ModelKind,
    type ModelRow,
    KIND_LABEL,
    bindUseCase,
    listModels,
    unbindUseCase,
} from '~/api/models'
import { DataTable } from '~/components/DataTable'
import { PageHeader } from '~/components/PageHeader'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Select } from '~/components/ui/select'
import { Skeleton } from '~/components/ui/skeleton'
import { useToast } from '~/components/ui/toast'

// 거버넌스 > 기능 카탈로그: 모든 AI use-case의 지도(map).
// 각 기능이 제품 어디(IA)에 쓰이고, 어떤 모델·프롬프트에 바인딩됐는지 한눈에 보여준다.
// 모델·프롬프트는 여기서 편집하지 않고 각 링크(모델/에이전트 설정/프롬프트)로 보낸다.
// GET /governance/catalog → taxonomy 순서로 정렬된 CatalogItem[].
interface CatalogItem {
    useCase: string
    kind: ModelKind
    label: string
    description: string
    // 제품 내 위치(IA). 예: "시민: 새 평가 > AI로 정의", "파이프라인: 문서 인입(PDF→VLM)".
    location: string
    // 활성 프롬프트 ref. null이면 코드 내장/없음.
    promptRef: string | null
    promptActiveVersion: number | null
    // model_bindings[useCase].model. null이면 용도 기본값을 상속.
    boundModel: string | null
    // 이 용도(kind)의 폴백 기본값 모델.
    defaultModel: string | null
}

// 용도(kind) 표시 순서 — taxonomy 흐름(채팅 → 임베딩 → 비전)을 따른다.
const KIND_ORDER: ModelKind[] = ['chat', 'embedding', 'vision']

// IA 위치 접두사(시민/콘솔/파이프라인/시스템) → 배지 톤. 위치는 사용자가 요청한
// 핵심 헤드라인이라 색으로도 표면을 구분한다. 알 수 없는 접두사는 info로 둔다.
const LOCATION_TONE: Record<string, BadgeTone> = {
    시민: 'info',
    콘솔: 'accent',
    파이프라인: 'point',
    시스템: 'neutral',
}

function locationTone(location: string): BadgeTone {
    const prefix = location.split(':')[0]?.trim() ?? ''
    return LOCATION_TONE[prefix] ?? 'info'
}

export function UseCaseCatalogPage() {
    const toast = useToast()
    const [items, setItems] = useState<CatalogItem[] | null>(null)
    const [models, setModels] = useState<ModelRow[]>([])
    // 현재 바인딩 변경 중인 use-case(중복 클릭 방지·비활성).
    const [busy, setBusy] = useState<string | null>(null)

    const reload = useCallback(() => {
        apiClient
            .get<CatalogItem[]>('/governance/catalog')
            .then(setItems)
            .catch((e) => {
                setItems([])
                toast.error((e as Error).message)
            })
    }, [toast])

    useEffect(() => {
        reload()
        listModels()
            .then((all) => setModels(all.filter((m) => m.active)))
            .catch((e) => toast.error((e as Error).message))
    }, [reload, toast])

    // 카탈로그에서 바로 모델 바인딩/해제(모델 페이지로 이동하지 않음). 성공 시 카탈로그 재로드.
    const rebind = async (it: CatalogItem, model: string) => {
        if (model === (it.boundModel ?? '')) return
        setBusy(it.useCase)
        try {
            if (model) {
                await bindUseCase(it.useCase, model)
                toast.success(`'${it.label}'에 '${model}' 모델을 바인딩했어요.`)
            } else {
                await unbindUseCase(it.useCase)
                toast.success(`'${it.label}' 바인딩을 해제했어요(기본값 사용).`)
            }
            reload()
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBusy(null)
        }
    }

    return (
        <div>
            <PageHeader
                title='기능 카탈로그'
                description='각 AI 기능이 어디에 쓰이는지 한눈에 봐요. 모델은 여기서 바로 바인딩하고, 프롬프트는 링크에서 편집해요.'
            />

            {items === null ? (
                <div className='space-y-2'>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className='h-12 w-full' />
                    ))}
                </div>
            ) : (
                KIND_ORDER.map((kind) => {
                    const group = items.filter((it) => it.kind === kind)
                    if (group.length === 0) return null
                    return (
                        <section key={kind} className='mb-6'>
                            <h2 className='text-foreground mb-2 text-sm font-semibold'>
                                {KIND_LABEL[kind]}
                                <span className='text-muted-foreground ml-1.5 font-normal'>
                                    ({group.length})
                                </span>
                            </h2>
                            <DataTable
                                columns={[
                                    '기능',
                                    'IA 위치',
                                    '모델',
                                    '프롬프트',
                                ]}
                                rows={group.map((it) => [
                                    <span key='fn' className='flex flex-col'>
                                        <span className='flex items-center gap-1.5'>
                                            <span className='text-foreground font-semibold'>
                                                {it.label}
                                            </span>
                                            <Badge
                                                tone='neutral'
                                                className='font-mono font-normal'>
                                                {it.useCase}
                                            </Badge>
                                        </span>
                                        {it.description ? (
                                            <span className='text-muted-foreground mt-0.5 text-xs'>
                                                {it.description}
                                            </span>
                                        ) : null}
                                    </span>,
                                    <Badge
                                        key='loc'
                                        tone={locationTone(it.location)}
                                        className='whitespace-normal'>
                                        {it.location}
                                    </Badge>,
                                    <span
                                        key='model'
                                        className='flex flex-col gap-1'>
                                        <Select
                                            size='sm'
                                            value={it.boundModel ?? ''}
                                            disabled={busy === it.useCase}
                                            onChange={(e) =>
                                                rebind(it, e.target.value)
                                            }
                                            aria-label={`${it.label} 모델 바인딩`}>
                                            <option value=''>
                                                기본값 사용
                                                {it.defaultModel
                                                    ? ` (${it.defaultModel})`
                                                    : ''}
                                            </option>
                                            {it.boundModel &&
                                            !models.some(
                                                (m) =>
                                                    m.kind === it.kind &&
                                                    m.name === it.boundModel
                                            ) ? (
                                                <option value={it.boundModel}>
                                                    {it.boundModel} (비활성/목록
                                                    외)
                                                </option>
                                            ) : null}
                                            {models
                                                .filter((m) => m.kind === it.kind)
                                                .map((m) => (
                                                    <option
                                                        key={m.id}
                                                        value={m.name}>
                                                        {m.name} ({m.modelId})
                                                    </option>
                                                ))}
                                        </Select>
                                    </span>,
                                    <span key='prompt' className='flex flex-col'>
                                        {it.promptRef ? (
                                            <>
                                                <span className='text-foreground font-mono text-xs'>
                                                    {it.promptRef}
                                                    {it.promptActiveVersion !=
                                                    null
                                                        ? ` · 활성 v${it.promptActiveVersion}`
                                                        : ''}
                                                </span>
                                                <Link
                                                    to={`/governance/prompts?ref=${encodeURIComponent(it.promptRef)}`}
                                                    className='text-primary mt-0.5 text-xs font-medium underline-offset-2 hover:underline'>
                                                    프롬프트
                                                </Link>
                                            </>
                                        ) : (
                                            <span className='text-muted-foreground'>
                                                코드 내장
                                            </span>
                                        )}
                                    </span>,
                                ])}
                                emptyMessage='이 용도의 기능이 없어요.'
                            />
                        </section>
                    )
                })
            )}
        </div>
    )
}
